import moment from 'moment'
import { fetchCompany } from '@/api/parameters'
import { moneda } from '@/utils/filtros'
import { lineBreakdownSection } from './lineBreakdownPdf'

export async function generatePostCostDashboardPdf(payload) {
  const pdfMake = require("pdfmake/build/pdfmake.js")
  const pdfFonts = require("pdfmake/build/vfs_fonts.js")
  pdfMake.vfs = pdfFonts.pdfMake.vfs

  // Datos de la empresa
  let company = payload.company || {}
  try {
    if (!company.name) {
      company = await fetchCompany()
    }
  } catch (e) {
    company = {}
  }

  const countryCode = company.country_code || 'cl'
  const companyName = company.fantasy || company.name || 'Runflow ERP'
  const companyRut = company.rut || ''
  const timezone = payload.serverTimezone || company.current_time_zone || 'America/Santiago'

  const formatMoney = (val) => moneda(val, countryCode)

  const startDateFormatted = payload.startDate ? moment(payload.startDate).format('DD/MM/YYYY') : '—'
  const endDateFormatted = payload.endDate ? moment(payload.endDate).format('DD/MM/YYYY') : '—'
  const dateModeLabel = payload.dateMode === 'completed' ? 'Por Cierre de OT' : 'Por Creación de OT'
  const lineName = payload.lineName || 'Todas las líneas'
  const periodLabel = `${payload.periodLabel || `${startDateFormatted} al ${endDateFormatted}`} · Línea: ${lineName}`

  const now = moment()
  const emissionDate = now.format('DD/MM/YYYY HH:mm')

  const dash = payload.dash || {}
  const totalSales = Number(dash.sales || 0)
  const totalReal = Number(dash.real || 0)
  const totalEstimated = Number(dash.estimated || 0)
  const realMargin = Number(dash.real_margin || 0)
  const estimatedMargin = Number(dash.estimated_margin || 0)
  const profit = Number(dash.profit || 0)
  const variance = Number(dash.variance || 0)
  const otCount = Number(dash.count || 0)

  const ppDelta = ((realMargin - estimatedMargin) * 100).toFixed(1)
  const deltaSign = Number(ppDelta) >= 0 ? '+' : ''

  // Cálculos de salud de margen
  const buckets = dash.buckets || {}
  const healthy = buckets.healthy || { count: 0, sales: 0 }
  const ok = buckets.ok || { count: 0, sales: 0 }
  const tight = buckets.tight || { count: 0, sales: 0 }
  const loss = buckets.loss || { count: 0, sales: 0 }
  const totalBandSales = (healthy.sales || 0) + (ok.sales || 0) + (tight.sales || 0) + (loss.sales || 0) || totalSales || 1

  const pct = (part, tot) => ((Number(part || 0) / Math.max(1, tot)) * 100).toFixed(1)

  const customers = dash.customers || []
  const sellers = dash.sellers || []
  const worst = dash.worst || []
  const best = dash.best || []
  const monthly = dash.monthly || []
  const breakdown = dash.breakdown || {}

  const marginColor = (m) => {
    const val = Number(m || 0)
    if (val >= 0.30) return '#047857' // Sano
    if (val >= 0.15) return '#0369A1' // Aceptable
    if (val >= 0.0) return '#B45309'  // Ajustado
    return '#BE123C'                 // Pérdida
  }

  // Document Definition
  const dd = {
    pageSize: 'LETTER',
    pageOrientation: 'landscape',
    pageMargins: [28, 22, 28, 22],
    footer: function(currentPage, pageCount) {
      return {
        margin: [28, 0, 28, 0],
        columns: [
          { text: 'RUNFLOW ERP · MÓDULO DE POSTCOSTEO Y RENTABILIDAD REAL DE PRODUCCIÓN', fontSize: 7, color: '#94A3B8', bold: true },
          { text: `Página ${currentPage} de ${pageCount}`, alignment: 'right', fontSize: 7, color: '#94A3B8', bold: true }
        ]
      }
    },
    content: [
      // ==========================================
      // PÁGINA 1: RESUMEN EJECUTIVO (ONE-PAGER)
      // ==========================================

      // 1. Header Banner - Midnight Slate (#0F172A)
      {
        table: {
          widths: ['*'],
          body: [
            [
              {
                fillColor: '#0F172A',
                border: [false, false, false, false],
                margin: [16, 11, 16, 11],
                columns: [
                  {
                    width: '*',
                    stack: [
                      {
                        text: 'RUNFLOW · INFORME EJECUTIVO DE POSTCOSTEO Y RENTABILIDAD REAL',
                        fontSize: 8,
                        bold: true,
                        color: '#00A650',
                        characterSpacing: 1.5,
                        margin: [0, 0, 0, 3]
                      },
                      {
                        text: `${companyName}`,
                        fontSize: 15,
                        bold: true,
                        color: '#FFFFFF',
                        margin: [0, 0, 0, 3]
                      },
                      {
                        text: `${companyRut ? 'RUT: ' + companyRut + '  ·  ' : ''}Rango Auditado: ${periodLabel}  ·  Criterio: ${dateModeLabel}`,
                        fontSize: 8.5,
                        color: '#CBD5E1'
                      }
                    ]
                  },
                  {
                    width: 230,
                    alignment: 'right',
                    stack: [
                      {
                        text: 'CONFIDENCIAL · COMITÉ DE COSTOS Y GERENCIA',
                        fontSize: 7.5,
                        bold: true,
                        color: '#FCA5A5',
                        margin: [0, 0, 0, 5]
                      },
                      {
                        text: `Emisión: ${emissionDate} hrs`,
                        fontSize: 8,
                        color: '#94A3B8',
                        margin: [0, 0, 0, 2]
                      },
                      {
                        text: `Zona Horaria: ${timezone}`,
                        fontSize: 7.5,
                        color: '#64748B'
                      }
                    ]
                  }
                ]
              }
            ]
          ]
        },
        margin: [0, 0, 0, 9],
        layout: {
          paddingLeft: () => 0,
          paddingRight: () => 0,
          paddingTop: () => 0,
          paddingBottom: () => 0
        }
      },

      // 2. Row of 4 Hero KPIs
      {
        table: {
          widths: ['25%', '25%', '25%', '25%'],
          body: [
            [
              // Card 1: Venta Cerrada
              {
                fillColor: '#0F172A',
                border: [false, false, false, false],
                margin: [10, 8, 10, 8],
                stack: [
                  { text: 'VENTA CERRADA (OT)', fontSize: 7, bold: true, color: '#00A650', characterSpacing: 1 },
                  { text: formatMoney(totalSales), fontSize: 14, bold: true, color: '#FFFFFF', margin: [0, 2, 0, 2] },
                  {
                    text: `${otCount} OTs finalizadas  ·  ${dateModeLabel}`,
                    fontSize: 7.5,
                    color: '#94A3B8'
                  }
                ]
              },
              // Card 2: Costo Real Total
              {
                fillColor: '#F8FAFC',
                border: [false, false, false, false],
                margin: [10, 8, 10, 8],
                stack: [
                  { text: 'COSTO REAL TOTAL', fontSize: 7, bold: true, color: '#64748B', characterSpacing: 1 },
                  { text: formatMoney(totalReal), fontSize: 14, bold: true, color: '#0F172A', margin: [0, 2, 0, 2] },
                  { text: `Teórico cotizado: ${formatMoney(totalEstimated)}`, fontSize: 7.5, color: '#64748B' }
                ]
              },
              // Card 3: Margen Real Ponderado
              {
                fillColor: '#F8FAFC',
                border: [false, false, false, false],
                margin: [10, 8, 10, 8],
                stack: [
                  { text: 'MARGEN REAL PONDERADO', fontSize: 7, bold: true, color: '#64748B', characterSpacing: 1 },
                  {
                    text: `${(realMargin * 100).toFixed(1)}%`,
                    fontSize: 14,
                    bold: true,
                    color: marginColor(realMargin),
                    margin: [0, 2, 0, 2]
                  },
                  {
                    text: `Teórico ${(estimatedMargin * 100).toFixed(1)}%  ·  ${deltaSign}${ppDelta} pp`,
                    fontSize: 7.5,
                    bold: true,
                    color: Number(ppDelta) >= 0 ? '#047857' : '#BE123C'
                  }
                ]
              },
              // Card 4: Resultado Operacional
              {
                fillColor: '#F8FAFC',
                border: [false, false, false, false],
                margin: [10, 8, 10, 8],
                stack: [
                  { text: 'RESULTADO OPERACIONAL', fontSize: 7, bold: true, color: '#64748B', characterSpacing: 1 },
                  {
                    text: formatMoney(profit),
                    fontSize: 14,
                    bold: true,
                    color: profit >= 0 ? '#047857' : '#BE123C',
                    margin: [0, 2, 0, 2]
                  },
                  {
                    text: `Desvío presupuestario: ${formatMoney(variance)}`,
                    fontSize: 7.5,
                    bold: true,
                    color: variance <= 0 ? '#047857' : '#BE123C'
                  }
                ]
              }
            ]
          ]
        },
        margin: [0, 0, 0, 8],
        layout: {
          defaultBorder: false,
          hLineWidth: () => 0,
          vLineWidth: () => 0,
          paddingLeft: () => 3,
          paddingRight: () => 3,
          paddingTop: () => 0,
          paddingBottom: () => 0
        }
      },

      // 3. Middle Row: Evolución Temporal (Chart) + Salud de Margen & Estructura
      {
        columns: [
          // Left: Chart Image or Monthly Table (width 430 pt)
          {
            width: 430,
            stack: [
              {
                text: 'EVOLUCIÓN TEMPORAL: VENTA VS COSTO TEÓRICO Y REAL',
                fontSize: 8,
                bold: true,
                color: '#475569',
                characterSpacing: 0.8,
                margin: [0, 0, 0, 4]
              },
              payload.chartImageBase64 ? {
                image: payload.chartImageBase64,
                width: 430,
                height: 155
              } : {
                table: {
                  headerRows: 1,
                  dontBreakRows: true,
                  widths: ['*', 75, 75, 75, 55],
                  body: [
                    [
                      { text: 'MES', fontSize: 7, bold: true, color: '#64748B', fillColor: '#F8FAFC' },
                      { text: 'VENTA', fontSize: 7, bold: true, color: '#64748B', fillColor: '#F8FAFC', alignment: 'right' },
                      { text: 'COSTO TEÓRICO', fontSize: 7, bold: true, color: '#64748B', fillColor: '#F8FAFC', alignment: 'right' },
                      { text: 'COSTO REAL', fontSize: 7, bold: true, color: '#64748B', fillColor: '#F8FAFC', alignment: 'right' },
                      { text: 'MARGEN', fontSize: 7, bold: true, color: '#64748B', fillColor: '#F8FAFC', alignment: 'right' }
                    ],
                    ...(monthly.slice(-6).map((m) => [
                      { text: m.month || '—', fontSize: 7.5, bold: true, color: '#0F172A' },
                      { text: formatMoney(m.sales || 0), fontSize: 7.5, alignment: 'right', color: '#0F172A' },
                      { text: formatMoney(m.estimated || 0), fontSize: 7.5, alignment: 'right', color: '#64748B' },
                      { text: formatMoney(m.real || 0), fontSize: 7.5, bold: true, alignment: 'right', color: '#0F172A' },
                      {
                        text: `${(Number(m.margin || 0) * 100).toFixed(1)}%`,
                        fontSize: 7.5,
                        bold: true,
                        alignment: 'right',
                        color: marginColor(m.margin)
                      }
                    ]))
                  ]
                },
                layout: {
                  hLineWidth: () => 0.5,
                  vLineWidth: () => 0,
                  hLineColor: () => '#E2E8F0',
                  paddingLeft: () => 5,
                  paddingRight: () => 5,
                  paddingTop: () => 4,
                  paddingBottom: () => 4
                }
              }
            ]
          },
          // Gap: 15 pt
          { width: 15, text: '' },
          // Right: Salud de Margen Real & Desglose Costos (width 290 pt)
          {
            width: 290,
            stack: [
              {
                text: 'DISTRIBUCIÓN DE SALUD DE MARGEN REAL',
                fontSize: 8,
                bold: true,
                color: '#475569',
                characterSpacing: 0.8,
                margin: [0, 0, 0, 4]
              },
              {
                table: {
                  widths: ['*', 65, 85],
                  body: [
                    [
                      { text: 'TRAMO DE MARGEN', fontSize: 7, bold: true, color: '#64748B', fillColor: '#F8FAFC' },
                      { text: 'OTs', fontSize: 7, bold: true, color: '#64748B', alignment: 'center', fillColor: '#F8FAFC' },
                      { text: 'MONTO (% DE VENTA)', fontSize: 7, bold: true, color: '#64748B', alignment: 'right', fillColor: '#F8FAFC' }
                    ],
                    [
                      { text: '● Sano (≥ 30%)', fontSize: 7.5, bold: true, color: '#047857' },
                      { text: `${healthy.count || 0} OT`, fontSize: 7.5, alignment: 'center', color: '#047857' },
                      { text: `${formatMoney(healthy.sales || 0)} (${pct(healthy.sales, totalBandSales)}%)`, fontSize: 7.5, bold: true, alignment: 'right', color: '#047857' }
                    ],
                    [
                      { text: '● Aceptable (15–30%)', fontSize: 7.5, bold: true, color: '#0369A1' },
                      { text: `${ok.count || 0} OT`, fontSize: 7.5, alignment: 'center', color: '#0369A1' },
                      { text: `${formatMoney(ok.sales || 0)} (${pct(ok.sales, totalBandSales)}%)`, fontSize: 7.5, bold: true, alignment: 'right', color: '#0369A1' }
                    ],
                    [
                      { text: '● Ajustado (0–15%)', fontSize: 7.5, bold: true, color: '#B45309' },
                      { text: `${tight.count || 0} OT`, fontSize: 7.5, alignment: 'center', color: '#B45309' },
                      { text: `${formatMoney(tight.sales || 0)} (${pct(tight.sales, totalBandSales)}%)`, fontSize: 7.5, bold: true, alignment: 'right', color: '#B45309' }
                    ],
                    [
                      { text: '● Pérdida (< 0%)', fontSize: 7.5, bold: true, color: '#BE123C' },
                      { text: `${loss.count || 0} OT`, fontSize: 7.5, alignment: 'center', color: '#BE123C' },
                      { text: `${formatMoney(loss.sales || 0)} (${pct(loss.sales, totalBandSales)}%)`, fontSize: 7.5, bold: true, alignment: 'right', color: '#BE123C' }
                    ]
                  ]
                },
                layout: {
                  hLineWidth: () => 0.5,
                  vLineWidth: () => 0,
                  hLineColor: () => '#E2E8F0',
                  paddingLeft: () => 5,
                  paddingRight: () => 5,
                  paddingTop: () => 4,
                  paddingBottom: () => 4
                }
              },
              // Micro Ticker Estructura Costos
              {
                margin: [0, 6, 0, 0],
                table: {
                  widths: ['*'],
                  body: [
                    [
                      {
                        fillColor: '#F1F5F9',
                        border: [false, false, false, false],
                        margin: [8, 5, 8, 5],
                        columns: [
                          { text: 'ESTRUCTURA:', fontSize: 7.5, bold: true, color: '#0F172A', width: 75 },
                          {
                            text: `Proc: ${formatMoney(breakdown.processes || 0)}  ·  Insumos: ${formatMoney(breakdown.supplies || 0)}  ·  Compras: ${formatMoney(breakdown.purchases || 0)}`,
                            fontSize: 7,
                            color: '#475569',
                            width: '*'
                          },
                          {
                            text: Number(breakdown.reprocesses || 0) > 0 ? `Reproc: ${formatMoney(breakdown.reprocesses)}` : 'Sin reprocesos',
                            fontSize: 7,
                            bold: Number(breakdown.reprocesses || 0) > 0,
                            color: Number(breakdown.reprocesses || 0) > 0 ? '#BE123C' : '#64748B',
                            alignment: 'right',
                            width: 80
                          }
                        ]
                      }
                    ]
                  ]
                },
                layout: {
                  defaultBorder: false,
                  hLineWidth: () => 0,
                  vLineWidth: () => 0
                }
              }
            ]
          }
        ],
        margin: [0, 0, 0, 9]
      },

      // 4. Bottom Row: Top 5 Clientes y Top 5 Ejecutivos (Zero overflow on Page 1)
      {
        columns: [
          // Table Left: Top 5 Clientes
          {
            width: 360,
            stack: [
              {
                text: 'TOP CLIENTES POR FACTURACIÓN CERRADA',
                fontSize: 8,
                bold: true,
                color: '#475569',
                characterSpacing: 0.8,
                margin: [0, 0, 0, 4]
              },
              {
                table: {
                  headerRows: 1,
                  dontBreakRows: true,
                  widths: [16, '*', 38, 70, 45],
                  body: [
                    [
                      { text: '#', fontSize: 7, bold: true, color: '#64748B', fillColor: '#F8FAFC', alignment: 'center' },
                      { text: 'CLIENTE', fontSize: 7, bold: true, color: '#64748B', fillColor: '#F8FAFC' },
                      { text: 'OTs', fontSize: 7, bold: true, color: '#64748B', fillColor: '#F8FAFC', alignment: 'center' },
                      { text: 'VENTA NETA', fontSize: 7, bold: true, color: '#64748B', fillColor: '#F8FAFC', alignment: 'right' },
                      { text: 'MARGEN', fontSize: 7, bold: true, color: '#64748B', fillColor: '#F8FAFC', alignment: 'right' }
                    ],
                    ...(customers.slice(0, 5).length ? customers.slice(0, 5).map((c, idx) => [
                      { text: String(idx + 1), fontSize: 7.5, color: '#64748B', alignment: 'center' },
                      { text: c.name || 'Sin nombre', fontSize: 7.5, bold: true, color: '#0F172A' },
                      { text: String(c.count || 0), fontSize: 7.5, alignment: 'center', color: '#475569' },
                      { text: formatMoney(c.sales || 0), fontSize: 7.5, bold: true, alignment: 'right', color: '#0F172A' },
                      {
                        text: `${(Number(c.margin || 0) * 100).toFixed(1)}%`,
                        fontSize: 7.5,
                        bold: true,
                        alignment: 'right',
                        color: marginColor(c.margin)
                      }
                    ]) : [
                      [{ text: 'Sin registros de clientes', colSpan: 5, alignment: 'center', fontSize: 7.5, color: '#94A3B8' }, {}, {}, {}, {}]
                    ])
                  ]
                },
                layout: {
                  hLineWidth: () => 0.5,
                  vLineWidth: () => 0,
                  hLineColor: () => '#E2E8F0',
                  paddingLeft: () => 4,
                  paddingRight: () => 4,
                  paddingTop: () => 4,
                  paddingBottom: () => 4
                }
              }
            ]
          },
          // Gap: 15 pt
          { width: 15, text: '' },
          // Table Right: Top 5 Ejecutivos Comerciales
          {
            width: 360,
            stack: [
              {
                text: 'TOP EJECUTIVOS COMERCIALES',
                fontSize: 8,
                bold: true,
                color: '#475569',
                characterSpacing: 0.8,
                margin: [0, 0, 0, 4]
              },
              {
                table: {
                  headerRows: 1,
                  dontBreakRows: true,
                  widths: [16, '*', 38, 70, 45],
                  body: [
                    [
                      { text: '#', fontSize: 7, bold: true, color: '#64748B', fillColor: '#F8FAFC', alignment: 'center' },
                      { text: 'EJECUTIVO', fontSize: 7, bold: true, color: '#64748B', fillColor: '#F8FAFC' },
                      { text: 'OTs', fontSize: 7, bold: true, color: '#64748B', fillColor: '#F8FAFC', alignment: 'center' },
                      { text: 'VENTA NETA', fontSize: 7, bold: true, color: '#64748B', fillColor: '#F8FAFC', alignment: 'right' },
                      { text: 'MARGEN', fontSize: 7, bold: true, color: '#64748B', fillColor: '#F8FAFC', alignment: 'right' }
                    ],
                    ...(sellers.slice(0, 5).length ? sellers.slice(0, 5).map((s, idx) => [
                      { text: String(idx + 1), fontSize: 7.5, color: '#64748B', alignment: 'center' },
                      { text: s.name || 'Sin nombre', fontSize: 7.5, bold: true, color: '#0F172A' },
                      { text: String(s.count || 0), fontSize: 7.5, alignment: 'center', color: '#475569' },
                      { text: formatMoney(s.sales || 0), fontSize: 7.5, bold: true, alignment: 'right', color: '#0F172A' },
                      {
                        text: `${(Number(s.margin || 0) * 100).toFixed(1)}%`,
                        fontSize: 7.5,
                        bold: true,
                        alignment: 'right',
                        color: marginColor(s.margin)
                      }
                    ]) : [
                      [{ text: 'Sin registros de ejecutivos', colSpan: 5, alignment: 'center', fontSize: 7.5, color: '#94A3B8' }, {}, {}, {}, {}]
                    ])
                  ]
                },
                layout: {
                  hLineWidth: () => 0.5,
                  vLineWidth: () => 0,
                  hLineColor: () => '#E2E8F0',
                  paddingLeft: () => 4,
                  paddingRight: () => 4,
                  paddingTop: () => 4,
                  paddingBottom: () => 4
                }
              }
            ]
          }
        ]
      },

      // ========================================================
      // PÁGINA 2: ANEXO DE AUDITORÍA DETALLADA (PAGE BREAK FORZADO)
      // ========================================================
      { text: '', pageBreak: 'before' },

      // Header Anexo
      {
        table: {
          widths: ['*'],
          body: [
            [
              {
                fillColor: '#F8FAFC',
                border: [false, false, false, false],
                margin: [12, 8, 12, 8],
                columns: [
                  {
                    text: 'ANEXO DE AUDITORÍA Y CONTROL DE DESVÍOS EN COSTOS DE PRODUCCIÓN',
                    fontSize: 9.5,
                    bold: true,
                    color: '#0F172A'
                  },
                  {
                    text: `${companyName}  ·  Período: ${periodLabel}`,
                    fontSize: 8,
                    color: '#64748B',
                    alignment: 'right'
                  }
                ]
              }
            ]
          ]
        },
        margin: [0, 0, 0, 10],
        layout: {
          paddingLeft: () => 0,
          paddingRight: () => 0,
          paddingTop: () => 0,
          paddingBottom: () => 0
        }
      },

      ...lineBreakdownSection({
        title: 'POSTCOSTEO POR LÍNEA DE NEGOCIO (COSTOS DE OT PRORRATEADOS SEGÚN VENTA)',
        rows: payload.lines || [],
        selectedName: lineName,
        columns: [
          { header: 'LÍNEA', value: (l) => l.name },
          { header: 'OTs', width: 45, align: 'center', value: (l) => String(l.count || 0) },
          { header: 'VENTA', width: 90, align: 'right', bold: true, value: (l) => formatMoney(l.sales || 0) },
          { header: 'COSTO TEÓRICO', width: 90, align: 'right', value: (l) => formatMoney(l.estimated || 0) },
          { header: 'COSTO REAL', width: 90, align: 'right', value: (l) => formatMoney(l.real || 0) },
          { header: 'RESULTADO', width: 90, align: 'right', value: (l) => formatMoney(l.profit || 0) },
          { header: 'MARGEN REAL', width: 65, align: 'right', bold: true, value: (l) => `${(Number(l.margin || 0) * 100).toFixed(1)}%` }
        ]
      }),

      // Tabla 1: Top OTs con Mayor Pérdida / Fuga de Margen
      {
        text: 'ÓRDENES DE TRABAJO CON MAYOR EROSIÓN / PÉRDIDA DE MARGEN (TOP DESVÍOS NEGATIVOS)',
        fontSize: 8.5,
        bold: true,
        color: '#BE123C',
        characterSpacing: 0.5,
        margin: [0, 0, 0, 4]
      },
      {
        table: {
          headerRows: 1,
          dontBreakRows: true,
          widths: [50, 150, '*', 80, 80, 60, 75],
          body: [
            [
              { text: 'OT #', fontSize: 7.5, bold: true, color: '#64748B', fillColor: '#FFF1F2', alignment: 'center' },
              { text: 'CLIENTE', fontSize: 7.5, bold: true, color: '#64748B', fillColor: '#FFF1F2' },
              { text: 'PROYECTO / REFERENCIA', fontSize: 7.5, bold: true, color: '#64748B', fillColor: '#FFF1F2' },
              { text: 'VENTA NETA', fontSize: 7.5, bold: true, color: '#64748B', fillColor: '#FFF1F2', alignment: 'right' },
              { text: 'COSTO REAL', fontSize: 7.5, bold: true, color: '#64748B', fillColor: '#FFF1F2', alignment: 'right' },
              { text: 'MARGEN %', fontSize: 7.5, bold: true, color: '#64748B', fillColor: '#FFF1F2', alignment: 'right' },
              { text: 'RESULTADO ($)', fontSize: 7.5, bold: true, color: '#64748B', fillColor: '#FFF1F2', alignment: 'right' }
            ],
            ...(worst.length ? worst.slice(0, 8).map((w) => [
              { text: `#${w.id}`, fontSize: 7.5, bold: true, color: '#0F172A', alignment: 'center' },
              { text: w.customer || 'Sin cliente', fontSize: 7.5, bold: true, color: '#0F172A' },
              { text: w.name || 'Sin referencia', fontSize: 7.5, color: '#475569' },
              { text: formatMoney(w.sales || 0), fontSize: 7.5, alignment: 'right', color: '#0F172A' },
              { text: formatMoney(w.real || 0), fontSize: 7.5, bold: true, alignment: 'right', color: '#BE123C' },
              {
                text: `${(Number(w.margin || 0) * 100).toFixed(1)}%`,
                fontSize: 7.5,
                bold: true,
                alignment: 'right',
                color: marginColor(w.margin)
              },
              {
                text: formatMoney(w.profit || (Number(w.sales || 0) - Number(w.real || 0))),
                fontSize: 7.5,
                bold: true,
                alignment: 'right',
                color: (w.profit || 0) >= 0 ? '#047857' : '#BE123C'
              }
            ]) : [
              [{ text: 'Sin órdenes con margen negativo en el período', colSpan: 7, alignment: 'center', fontSize: 7.5, color: '#94A3B8' }, {}, {}, {}, {}, {}, {}]
            ])
          ]
        },
        layout: {
          hLineWidth: () => 0.5,
          vLineWidth: () => 0,
          hLineColor: () => '#FCA5A5',
          paddingLeft: () => 5,
          paddingRight: () => 5,
          paddingTop: () => 3.5,
          paddingBottom: () => 3.5
        },
        margin: [0, 0, 0, 10]
      },

      // Tabla 2: Top OTs con Mejor Ejecución de Rentabilidad
      {
        text: 'ÓRDENES DE TRABAJO CON MEJOR EJECUCIÓN DE RENTABILIDAD (MAYOR UTILIDAD)',
        fontSize: 8.5,
        bold: true,
        color: '#047857',
        characterSpacing: 0.5,
        margin: [0, 0, 0, 4]
      },
      {
        table: {
          headerRows: 1,
          dontBreakRows: true,
          widths: [50, 150, '*', 80, 80, 60, 75],
          body: [
            [
              { text: 'OT #', fontSize: 7.5, bold: true, color: '#64748B', fillColor: '#ECFDF5', alignment: 'center' },
              { text: 'CLIENTE', fontSize: 7.5, bold: true, color: '#64748B', fillColor: '#ECFDF5' },
              { text: 'PROYECTO / REFERENCIA', fontSize: 7.5, bold: true, color: '#64748B', fillColor: '#ECFDF5' },
              { text: 'VENTA NETA', fontSize: 7.5, bold: true, color: '#64748B', fillColor: '#ECFDF5', alignment: 'right' },
              { text: 'COSTO REAL', fontSize: 7.5, bold: true, color: '#64748B', fillColor: '#ECFDF5', alignment: 'right' },
              { text: 'MARGEN %', fontSize: 7.5, bold: true, color: '#64748B', fillColor: '#ECFDF5', alignment: 'right' },
              { text: 'UTILIDAD ($)', fontSize: 7.5, bold: true, color: '#64748B', fillColor: '#ECFDF5', alignment: 'right' }
            ],
            ...(best.length ? best.slice(0, 8).map((b) => [
              { text: `#${b.id}`, fontSize: 7.5, bold: true, color: '#0F172A', alignment: 'center' },
              { text: b.customer || 'Sin cliente', fontSize: 7.5, bold: true, color: '#0F172A' },
              { text: b.name || 'Sin referencia', fontSize: 7.5, color: '#475569' },
              { text: formatMoney(b.sales || 0), fontSize: 7.5, alignment: 'right', color: '#0F172A' },
              { text: formatMoney(b.real || 0), fontSize: 7.5, alignment: 'right', color: '#64748B' },
              {
                text: `${(Number(b.margin || 0) * 100).toFixed(1)}%`,
                fontSize: 7.5,
                bold: true,
                alignment: 'right',
                color: '#047857'
              },
              {
                text: formatMoney(b.profit || (Number(b.sales || 0) - Number(b.real || 0))),
                fontSize: 7.5,
                bold: true,
                alignment: 'right',
                color: '#047857'
              }
            ]) : [
              [{ text: 'Sin órdenes para rankear en el período', colSpan: 7, alignment: 'center', fontSize: 7.5, color: '#94A3B8' }, {}, {}, {}, {}, {}, {}]
            ])
          ]
        },
        layout: {
          hLineWidth: () => 0.5,
          vLineWidth: () => 0,
          hLineColor: () => '#A7F3D0',
          paddingLeft: () => 5,
          paddingRight: () => 5,
          paddingTop: () => 3.5,
          paddingBottom: () => 3.5
        },
        margin: [0, 0, 0, 10]
      },

      // Tabla 3: Estructura Detallada de Costos de Planta
      {
        text: 'ESTRUCTURA DETALLADA DEL COSTO REAL DE PLANTA',
        fontSize: 8.5,
        bold: true,
        color: '#475569',
        characterSpacing: 0.5,
        margin: [0, 0, 0, 4]
      },
      {
        table: {
          headerRows: 1,
          dontBreakRows: true,
          widths: ['*', 100, 90, 90, 150],
          body: [
            [
              { text: 'RUBRO DE COSTO', fontSize: 7.5, bold: true, color: '#64748B', fillColor: '#F8FAFC' },
              { text: 'MONTO REAL TOTAL', fontSize: 7.5, bold: true, color: '#64748B', fillColor: '#F8FAFC', alignment: 'right' },
              { text: '% DEL COSTO TOTAL', fontSize: 7.5, bold: true, color: '#64748B', fillColor: '#F8FAFC', alignment: 'right' },
              { text: '% SOBRE LA VENTA', fontSize: 7.5, bold: true, color: '#64748B', fillColor: '#F8FAFC', alignment: 'right' },
              { text: 'OBSERVACIONES OPERATIVAS', fontSize: 7.5, bold: true, color: '#64748B', fillColor: '#F8FAFC' }
            ],
            [
              { text: 'Mano de Obra y Procesos de Taller', fontSize: 7.5, bold: true, color: '#0F172A' },
              { text: formatMoney(breakdown.processes || 0), fontSize: 7.5, bold: true, alignment: 'right', color: '#0F172A' },
              { text: `${pct(breakdown.processes, totalReal)}%`, fontSize: 7.5, alignment: 'right', color: '#475569' },
              { text: `${pct(breakdown.processes, totalSales)}%`, fontSize: 7.5, alignment: 'right', color: '#64748B' },
              { text: 'Costos estándar aplicados por etapa operativa', fontSize: 7, color: '#64748B' }
            ],
            [
              { text: 'Reprocesos y Correcciones de Calidad', fontSize: 7.5, bold: true, color: Number(breakdown.reprocesses || 0) > 0 ? '#BE123C' : '#0F172A' },
              { text: formatMoney(breakdown.reprocesses || 0), fontSize: 7.5, bold: true, alignment: 'right', color: Number(breakdown.reprocesses || 0) > 0 ? '#BE123C' : '#0F172A' },
              { text: `${pct(breakdown.reprocesses, totalReal)}%`, fontSize: 7.5, alignment: 'right', color: Number(breakdown.reprocesses || 0) > 0 ? '#BE123C' : '#475569' },
              { text: `${pct(breakdown.reprocesses, totalSales)}%`, fontSize: 7.5, alignment: 'right', color: '#64748B' },
              { text: dash.reprocess_ots ? `${dash.reprocess_ots} OTs con retrabajos registrados` : 'Sin reprocesos en el período', fontSize: 7, color: dash.reprocess_ots ? '#BE123C' : '#64748B' }
            ],
            [
              { text: 'Materiales e Insumos de Bodega', fontSize: 7.5, bold: true, color: '#0F172A' },
              { text: formatMoney(breakdown.supplies || 0), fontSize: 7.5, bold: true, alignment: 'right', color: '#0F172A' },
              { text: `${pct(breakdown.supplies, totalReal)}%`, fontSize: 7.5, alignment: 'right', color: '#475569' },
              { text: `${pct(breakdown.supplies, totalSales)}%`, fontSize: 7.5, alignment: 'right', color: '#64748B' },
              { text: 'Despachos de stock para producción', fontSize: 7, color: '#64748B' }
            ],
            [
              { text: 'Compras Directas a Proveedores', fontSize: 7.5, bold: true, color: '#0F172A' },
              { text: formatMoney(breakdown.purchases || 0), fontSize: 7.5, bold: true, alignment: 'right', color: '#0F172A' },
              { text: `${pct(breakdown.purchases, totalReal)}%`, fontSize: 7.5, alignment: 'right', color: '#475569' },
              { text: `${pct(breakdown.purchases, totalSales)}%`, fontSize: 7.5, alignment: 'right', color: '#64748B' },
              { text: 'Servicios de terceros y suministros directos', fontSize: 7, color: '#64748B' }
            ],
            [
              { text: 'TOTAL COSTO REAL INCURRIDO', fontSize: 7.5, bold: true, color: '#0F172A', fillColor: '#F8FAFC' },
              { text: formatMoney(totalReal), fontSize: 7.5, bold: true, alignment: 'right', color: '#0F172A', fillColor: '#F8FAFC' },
              { text: '100.0%', fontSize: 7.5, bold: true, alignment: 'right', color: '#0F172A', fillColor: '#F8FAFC' },
              { text: `${pct(totalReal, totalSales)}%`, fontSize: 7.5, bold: true, alignment: 'right', color: '#0F172A', fillColor: '#F8FAFC' },
              { text: `Margen global: ${(realMargin * 100).toFixed(1)}%`, fontSize: 7, bold: true, color: marginColor(realMargin), fillColor: '#F8FAFC' }
            ]
          ]
        },
        layout: {
          hLineWidth: () => 0.5,
          vLineWidth: () => 0,
          hLineColor: () => '#E2E8F0',
          paddingLeft: () => 5,
          paddingRight: () => 5,
          paddingTop: () => 3.5,
          paddingBottom: () => 3.5
        }
      }
    ]
  }

  const fileName = `Reporte_Ejecutivo_Postcosteo_${payload.startDate || 'inicio'}_a_${payload.endDate || 'fin'}.pdf`
  pdfMake.createPdf(dd).download(fileName)
}
